import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api, uploadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader } from "@/components/EmptyState";
import { LICENSE_TYPES } from "@/lib/constants";
import { Upload, ArrowLeft } from "lucide-react";

const LABELS = {
  name: "Business Name", business_email: "Business Email", logo_file_id: "Business Logo",
  owner_name: "Owner Name", address: "Business Address", license_type: "License Type",
  license_number: "License Number", license_issue_date: "License Issue Date",
  license_expiry_date: "License Expiry Date", license_doc_file_id: "License Document",
};

export default function Resubmit() {
  const navigate = useNavigate();
  const { user, business, loading, refresh } = useAuth();
  const [f, setF] = useState({});
  const [docName, setDocName] = useState("");
  const [logoName, setLogoName] = useState("");
  const [busy, setBusy] = useState(false);

  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (business?.registration_status !== "rejected") return <Navigate to="/gate" replace />;

  // If admin flagged specific fields, only those; otherwise allow the full editable set.
  const flagged = business?.rejected_fields?.length ? business.rejected_fields : Object.keys(LABELS);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const val = (k) => (f[k] !== undefined ? f[k] : business?.[k] ?? "");

  const upload = async (file, purpose, setName, key) => {
    try {
      const res = await uploadFile(file, purpose);
      set(key, res.file_id);
      setName(res.filename);
      toast.success("Uploaded");
    } catch { toast.error("Upload failed"); }
  };

  const submit = async () => {
    const payload = {};
    flagged.forEach((k) => { if (f[k] !== undefined) payload[k] = f[k]; });
    if (Object.keys(payload).length === 0) { toast.error("Please update at least one requested field"); return; }
    setBusy(true);
    try {
      await api.post("/business/resubmit", payload);
      await refresh();
      toast.success("Resubmitted for verification");
      navigate("/gate");
    } catch (e) { toast.error(e.response?.data?.detail || "Resubmission failed"); } finally { setBusy(false); }
  };

  const renderField = (k) => {
    if (k === "license_type") return (
      <div key={k}><Label>{LABELS[k]}</Label>
        <Select value={val(k)} onValueChange={(v) => set(k, v)}>
          <SelectTrigger data-testid="resubmit-license-type" className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>{LICENSE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    );
    if (k === "address") return (
      <div key={k}><Label>{LABELS[k]}</Label>
        <Textarea data-testid="resubmit-address" className="mt-1" value={val(k)} onChange={(e) => set(k, e.target.value)} /></div>
    );
    if (k === "logo_file_id" || k === "license_doc_file_id") {
      const isLogo = k === "logo_file_id";
      return (
        <div key={k}><Label>{LABELS[k]}</Label>
          <label className="mt-1 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-[#E5E0D8] px-4 py-3 text-sm text-stone-500 hover:border-[#0D5C3A]">
            <Upload className="h-4 w-4" /> {(isLogo ? logoName : docName) || `Upload new ${isLogo ? "logo" : "document"}`}
            <input data-testid={`resubmit-${isLogo ? "logo" : "doc"}-input`} type="file" accept={isLogo ? "image/*" : "image/*,.pdf"} className="hidden"
              onChange={(e) => e.target.files[0] && upload(e.target.files[0], isLogo ? "logo" : "license", isLogo ? setLogoName : setDocName, k)} />
          </label>
        </div>
      );
    }
    const type = k.includes("date") ? "date" : k === "business_email" ? "email" : "text";
    return (
      <div key={k}><Label>{LABELS[k]}</Label>
        <Input data-testid={`resubmit-${k}`} type={type} className="mt-1" value={val(k)} onChange={(e) => set(k, e.target.value)} /></div>
    );
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] rv-grain">
      <div className="mx-auto max-w-2xl px-6 py-10">
        <button onClick={() => navigate("/gate")} className="mb-6 inline-flex items-center gap-1 text-sm text-stone-500 hover:text-[#0D5C3A]"><ArrowLeft className="h-4 w-4" /> Back to status</button>
        <h1 className="font-heading text-3xl font-extrabold text-stone-900">Correct & Resubmit</h1>
        <p className="mt-1 text-stone-500">Update the requested details below and resubmit for verification. Your other information is preserved.</p>
        {business?.rejection_reason && (
          <div className="mt-4 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700"><b>Admin note:</b> {business.rejection_reason}</div>
        )}
        <div className="mt-6 space-y-4 rounded-2xl border border-[#E5E0D8] bg-white p-7 shadow-sm">
          {flagged.map(renderField)}
          <Button data-testid="resubmit-submit-btn" disabled={busy} onClick={submit} className="w-full bg-[#C85A32] hover:bg-[#a8481f]">
            Resubmit for Verification
          </Button>
        </div>
      </div>
    </div>
  );
}
